import { WindowAppState, WindowUserInfo, windowAppStateSchema, windowCCBUserInfoSchema } from '@fe-core/api';
import * as v from 'valibot';

declare global {
  interface Window {
    appState: unknown;
    ccbUserInfo: unknown;
  }
}

export type WindowObject = {
  appState: WindowAppState;
  userInfo: WindowUserInfo;
};

const getWindowAppState = async (
  settings?: GetParsedWindowObjectSettings,
) => {
  if (settings && settings.useRemoteState && import.meta.env.MODE === 'development') {
    console.info('fe-core-injected | using remote app state');

    const remoteStateRaw = await fetch(settings.useRemoteState.url);
    const response = await remoteStateRaw.json();

    return v.parse(windowAppStateSchema, response.remote.appState);
  }

  const maybeAppState = window?.appState;
  return v.parse(windowAppStateSchema, maybeAppState);
};

const getWindowCCBUserInfo = async (
  settings?: GetParsedWindowObjectSettings,
) => {
  if (settings && settings.useRemoteState && import.meta.env.MODE === 'development') {
    console.info('fe-core-injected | using remote user info');

    const remoteStateRaw = await fetch(settings.useRemoteState.url);
    const response = await remoteStateRaw.json();

    return v.parse(windowCCBUserInfoSchema, response.remote.ccbUserInfo);
  }

  const maybeCcbUserInfo = window?.ccbUserInfo;
  return v.parse(windowCCBUserInfoSchema, maybeCcbUserInfo);
};

type GetParsedWindowObjectSettings = {
  useRemoteState: {
    url: string;
  }
}
export const getParsedWindowObject = async (
  settings?: GetParsedWindowObjectSettings,
) => {
  const appState = await getWindowAppState(settings);
  const userInfo = await getWindowCCBUserInfo(settings);
  return {
    appState,
    userInfo,
  };
};

/**
 * getWindowPortalAccess.
 * ___
 * - **userInfo**: accepts a userInfo object, retrievable from getParsedWindowObject.userInfo.
 */
export const getPortalAccess = (userInfo: WindowUserInfo) => {
  const portalAccess = userInfo.portalAccess;

  if (!portalAccess) {
    return {
      isBillingAvailable: false,
      isBoAvailable: false,
      isCpAvailable: false,
      isMultiCpAdmin: false,
      isTpAvailable: false,
      isCatalogBuilderAvailable: false,
      cpCount: 0,
    };
  }

  return {
    isBillingAvailable: portalAccess.isBillingAvailable ?? false,
    isBoAvailable: portalAccess.isBoAvailable ?? false,
    isCpAvailable: portalAccess.isCpAvailable ?? false,
    isMultiCpAdmin: portalAccess.isMultiCpAdmin ?? false,
    isTpAvailable: portalAccess.isTpAvailable ?? false,
    isCatalogBuilderAvailable: portalAccess.isCatalogBuilderAvailable ?? false,
    cpCount: portalAccess.cpCount ?? 0,
  };
};
